import { Component, HostListener, OnInit } from '@angular/core';
import { getPOSSoftwareDisplay, OrganizationConfig } from '../../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { OrgPOSSoftware } from '../../../../../../models/organization-config-enums';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { TaxRate } from '../../../../../../models/tax-rate';
import { TaxRateService } from '../../../../../../services/tax-rate/tax-rate.service';
import { propExists, propExistsAndIsNonEmpty } from '../../../../../../utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  GiftCardRecipientTextPreviewModalComponent
} from './modals/gift-card-recipient-text-preview-modal/gift-card-recipient-text-preview-modal.component';


@Component({
  selector: 'app-gift-card-sales-config',
  templateUrl: './gift-card-sales-config.component.html',
  styleUrls: ['./gift-card-sales-config.component.css']
})
export class GiftCardSalesConfigComponent implements OnInit, ComponentHasUnsavedChanges {
  public orgConfig: OrganizationConfig = null;

  public taxRateList: TaxRate[] = null;

  public loadingOrgConfig = true;
  public loadingTaxRates = true;
  public savingOrgConfig = false;

  public successMessage = '';
  public errorMessage = '';
  public errors: any = {};
  public unsavedChanges = false;

  public serverError: number = null;

  public readonly OrgPOSSoftware = OrgPOSSoftware;
  public readonly getPOSSoftwareDisplay = getPOSSoftwareDisplay;

  constructor(
    public orgConfigService: OrganizationConfigService,
    private taxRateService: TaxRateService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    this.loadOrganizationConfig();
    this.loadTaxRates();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  /**
   * Save the updated organization config to the API.
   */
  public saveOrganizationConfig() {
    this.successMessage = '';
    this.errors = {};
    this.errorMessage = '';

    const fieldsToUpdate: any = {
      allow_gift_card_sales: this.orgConfig.allow_gift_card_sales
    };
    if (this.orgConfig.allow_gift_card_sales) {
      if (this.orgConfig.pos_software === OrgPOSSoftware.square) {
        if (this.orgConfig.external_gift_card_purchase_url.trim() === '') {
          this.errors.external_gift_card_purchase_url = 'This field is required.';
          return;
        }
        fieldsToUpdate.external_gift_card_purchase_url = this.orgConfig.external_gift_card_purchase_url;
      }
      if (this.orgConfig.pos_software === OrgPOSSoftware.lightspeed) {
        fieldsToUpdate.max_gift_card_future_delivery_date_days = this.orgConfig.max_gift_card_future_delivery_date_days;
        fieldsToUpdate.gift_card_giver_terms_and_conditions = this.orgConfig.gift_card_giver_terms_and_conditions;
        fieldsToUpdate.min_gift_card_value = this.orgConfig.min_gift_card_value;
        fieldsToUpdate.max_gift_card_value = this.orgConfig.max_gift_card_value;
        fieldsToUpdate.gift_card_tax_rate_id = this.orgConfig.gift_card_tax_rate_id;
        fieldsToUpdate.gift_card_recipient_text = this.orgConfig.gift_card_recipient_text;
      }
    }

    this.savingOrgConfig = true;

    this.orgConfigService
      .updateOrgConfig(fieldsToUpdate)
      .then(updatedOrgConf => {
        this.orgConfig = updatedOrgConf;
        this.successMessage = 'Gift card sales configuration updated successfully.';
        this.unsavedChanges = false;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to change this configuration.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.savingOrgConfig = false; });
  }

  public loadTaxRates() {
    this.loadingTaxRates = true;

    this.taxRateService
      .getTaxRates(1, 100)
      .then(response => {
        this.taxRateList = response.results;

        this.setInitialTaxRate();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingTaxRates = false; });
  }

  public taxRateRemoved(taxRateId: number) {
    if (!propExists(this.taxRateList)) { return true; }

    for (const taxRate of this.taxRateList) {
      if (taxRate.id === taxRateId) { return false; }
    }

    return true;
  }

  public openGiftCardRecipientTextPreviewModal() {
    const modalRef = this.modalService.open(
      GiftCardRecipientTextPreviewModalComponent
    );
    modalRef.componentInstance.orgConfig = this.orgConfig;
  }

  /**
   * Load the organization config from the API.
   *
   * We do not use the local-stored copy, in order to avoid pop-in of changed
   * values.
   */
  private loadOrganizationConfig() {
    this.loadingOrgConfig = true;

    this.orgConfigService
      .loadOrganizationConfig()
      .then(() => {
        this.orgConfig = this.orgConfigService.getOrganizationConfig();

        if (this.orgConfig.pos_software === OrgPOSSoftware.none) {
          this.orgConfig.allow_gift_card_sales = false;
        }

        this.setInitialTaxRate();

        this.loadingOrgConfig = false;
      });
  }

  private setInitialTaxRate() {
    // Set the initial tax rate to the first one in the list, if a tax rate
    // is not already configured.
    const willSet = (
      propExistsAndIsNonEmpty(this.taxRateList) &&
      propExists(this.orgConfig) &&
      (this.orgConfig.gift_card_tax_rate_id === null)
    );
    if (willSet) {
      this.orgConfig.gift_card_tax_rate_id = this.taxRateList[0].id;
    }
  }
}
